import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Clock, Eye, Gift, Sparkles, Users } from 'lucide-react';
import styles from './index.module.less';
import classNames from 'classnames';
import { fetchInsMonitorSearch, fetchInsRecentUserSub, fetchRecentSearchData } from '@/lib/service';
import { InsAccountModel, RecentSearchUserList, RecentSubStatusModel } from '@/types/home';
import { useUserProvider } from '@/context/UserProvider';
import UserDashboard from '../Dashboard/UserDashboard';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { imgUrl } from '@/shared/utils';
import { useTranslation } from 'next-i18next';
import { Button, message, Tooltip } from 'antd';
import { cn } from '@/lib/utils';
import useMediaNameSync from '@/lib/hooks/useMediaNameSync';
import { QuestionCircleFilled } from '@ant-design/icons';
import { useRouter } from 'next/router';
import { Turnstile } from '@marsidev/react-turnstile';

const LocalImage = dynamic(() => import('../Common/LocalImage'), { ssr: true });
const FAQSection = dynamic(() => import('./FAQSection'), { ssr: true });
const ExploreMoreToolsSection = dynamic(() => import('./ExploreMoreToolsSection'), { ssr: true });
const UserReviewsSection = dynamic(() => import('./UserReviewsSection'), { ssr: true });
const HowItWorksSection = dynamic(() => import('./HowItWorksSection'), { ssr: true });
const UpgradeModal = dynamic(() => import('./UpgradeModal'), { ssr: false });
const GenderDistribution = dynamic(() => import('./GenderDistribution'), { ssr: false });
// CLS 优化：不使用 dynamic 导入，确保与结果列表在同一帧渲染
import InstagramUserInfo from './InstagramUserInfo';
const UserUpdates = dynamic(() => import('./UserUpdates/index'), { ssr: true });
const ContrastCheck = dynamic(() => import('./ContrastCheck'), { ssr: true });
const RealStore = dynamic(() => import('./RealStore'), { ssr: true });
const ProductionFunction = dynamic(() => import('./ProductionFunc'), { ssr: true });
const CommonSimpleModal = dynamic(() => import('../Common/CommonSimpleModal'), { ssr: false });
const RefueingPkg = dynamic(() => import('./RefueingPkg'), { ssr: true });
import ResultsSkeleton from './ResultsSkeleton';
import LoadMoreSkeleton from './LoadMoreSkeleton';

// const CommonSimpleModal = dynamic(() => import('../Common/CommonSimpleModal'), { ssr: false })
// const PlanModal = dynamic(() => import('./PlanModal'), { ssr: false })

const images = [
  'https://res-ins.pumpsoul.com/ins_image/1715226267021510004.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715231944765698112.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715232052557283516.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715232014132880270.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715271421881722942.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715232201493612730.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715233527880123759.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715234676612624393.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715270486218568131.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715232601673693741.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715232171016468256.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715231972925490962.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715268339164740710.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715234717494124470.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715271458106769031.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715263176240124166.jpg',
  'https://res-ins.pumpsoul.com/ins_image/1715233472060278219.jpg',
];

const getRandomImages = () => {
  const randomImages = [];
  for (let i = 0; i < 3; i++) {
    const randomIndex = Math.floor(Math.random() * images.length);
    randomImages.push(images[randomIndex]);
  }
  return randomImages;
};

const isNotExpired = (expired_time: string | number) => {
  return expired_time && new Date(expired_time) > new Date();
};

export default function RecentlyFollowedTool() {
  const inputSectionRef = useRef(null);
  const [username, setUsername] = useMediaNameSync('', {
    removeAfterRead: true,
  });
  const [showUserName, setShowUserName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const [activeTab, setActiveTab] = useState<'follows' | 'followers'>('follows');
  const [followingData, setFollowingData] = useState<RecentSearchUserList>(null);
  const [followerData, setFollowerData] = useState<RecentSearchUserList>(null);
  const [currentInsUser, setCurrentInsUser] = useState<InsAccountModel>(null);
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const [followingPageNo, setFollowingPageNo] = useState(1);
  const [followerPageNo, setFollowerPageNo] = useState(1);
  const [showSubscriptionLimit, setShowSubscriptionLimit] = useState(false);
  const { t } = useTranslation();
  const [openPrivateAccount, setOpenPrivateAccount] = useState<any>(null);
  const router = useRouter();
  const [hasNoData, setHasNoData] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<any>(null);
  const nextCallRef = useRef<any>(null);
  const isLangLanguage = ['th', 'ja', 'es'].includes(router.locale);
  const [isFirstSearch, setIsFirstSearch] = useState(true);
  // 加油包
  const [openRefueingPkg, setOpenRefueingPkg] = useState(false);
  // CLS 优化：当弹窗打开时保持骨架屏显示，避免布局位移
  const [keepSkeletonVisible, setKeepSkeletonVisible] = useState(false);
  // CLS 优化：控制内容显示时机，确保所有状态在同一帧更新
  const [showContent, setShowContent] = useState(false);

  const needTurnstile = useMemo(() => {
    let currentTagNeed;
    if (activeTab === 'follows') {
      currentTagNeed = !followingData?.items?.length;
    } else {
      currentTagNeed = !followerData?.items?.length;
    }
    return !isLogin && currentTagNeed;
  }, [isLogin, activeTab, followingData, followerData]);

  const words = useMemo(() => {
    return [
      { text: t('Followed'), color: '#10B981' },
      { text: t('Follow'), color: '#F97316' },
      { text: t('Under Wraps'), color: '#7C3AED' },
      { text: t('Followed'), color: '#10B981' },
    ];
  }, []);

  const currentList = useMemo(() => {
    if (activeTab === 'follows') {
      return followingData;
    } else {
      return followerData;
    }
  }, [followingData, followerData, activeTab]);

  const [userSub, setUserSub] = useState<RecentSubStatusModel>(null);
  const validSubscription = useMemo(() => isNotExpired(userSub?.expired_time), [userSub?.expired_time]);
  // const hasLoginAndSubNormal = useMemo(() => hasNoData, [hasNoData]);

  const isPremium = !!(isLogin && validSubscription);

  useEffect(() => {
    if (isLogin) {
      fetchInsRecentUserSub().then((res) => {
        setUserSub(res.data);
        if (isNotExpired(res.data?.expired_time)) {
          setShowSubscriptionLimit(false);
        }
      });
    } else {
      setUserSub(null);
    }
  }, [isLogin]);

  useEffect(() => {
    if (isLogin && userSub) {
      // 最近订阅
      fetchInsRecentUserSub().then((res) => {
        setUserSub(res.data);
      });
    }
  }, []);

  // 是否当天大于第二次点击
  const isSecondClick = (): boolean => {
    const today = new Date().toISOString().split('T')[0];
    const clickRecord = localStorage.getItem('daily_click_count');
    const record = clickRecord ? JSON.parse(clickRecord) : { date: '', count: 0 };

    if (record.date !== today) {
      // 新的一天，重置计数
      record.date = today;
      record.count = 1;
    } else {
      // 同一天，增加计数
      record.count += 1;
    }

    localStorage.setItem('daily_click_count', JSON.stringify(record));
    return record.count >= 2;
  };

  const handleReveal = async ({
    waitToken = true,
    needTurnstile,
    turnstileToken,
    isFollowing,
    pageNo,
    username,
    isBtnClick,
    data,
  }: {
    waitToken?: boolean;
    needTurnstile?: boolean;
    turnstileToken?: string | null;
    isFollowing: boolean;
    pageNo: number;
    username: string;
    isBtnClick?: boolean;
    data?: any;
  }) => {
    if (!username) return;

    // 第二次
    if (isBtnClick && isSecondClick()) {
      // 无有效订阅时，显示订阅引导弹窗
      if (!validSubscription) {
        setShowSubscriptionLimit(true);
        return;
      }
    }

    setStage(1);
    await new Promise((resolve) => setTimeout(resolve, 10));

    setStage(2);
    setIsLoading(true);

    if (!isLogin && waitToken) {
      nextCallRef.current = (needTurnstile: boolean, turnstileToken: string | null) =>
        handleReveal({
          waitToken: false,
          needTurnstile,
          turnstileToken,
          isFollowing,
          pageNo,
          username,
          isBtnClick,
          data,
        });
      return;
    }
    if (!turnstileToken && needTurnstile) {
      setIsLoading(false);
      setStage(0);
      return message.warning(t('Please verify before continuing.'));
    }
    const dataRes = await fetchRecentSearchData(isFollowing, pageNo, username, needTurnstile && turnstileToken, {
      isPremium,
      bizFuncName: 'recent_follow',
    });
    if (pageNo === 1) {
      if (dataRes?.code === 0) {
        const dom = document.getElementById('search-result-user-info');

        // CLS 优化核心：doShowContent 函数将所有影响布局的状态更新集中在一起
        // 根据 React 18 自动批处理机制，同一同步函数内的多个 setState 只触发一次渲染
        // 这样骨架屏消失和内容出现在同一帧内完成，避免 CLS
        const doShowContent = () => {
          // 0. 设置用户信息 - 必须在同一批次内，否则会造成 CLS
          if (data) {
            setCurrentInsUser(data);
          }
          // 1. 设置数据
          if (isFollowing) {
            setFollowingData(dataRes?.data);
          } else {
            setFollowerData(dataRes?.data);
          }
          // 2. 更新其他状态
          setIsFirstSearch(false);
          setStage(0);
          setShowUserName(username);
          // 3. 关闭 loading
          setIsLoading(false);
          // 4. 设置显示标志
          setShowContent(true);

          // 5. 根据返回状态显示对应弹窗
          const status = dataRes?.data?.current_status;
          if (status === 1) {
            setKeepSkeletonVisible(true); // CLS: 弹窗期间保持骨架屏
            setShowSubscriptionLimit(true);
          } else if (status === 2) {
            setKeepSkeletonVisible(true); // CLS: 弹窗期间保持骨架屏
            setOpenRefueingPkg(true);
          }
        };

        // 先滚动到目标位置，滚动完成后再显示内容
        // 目标：让 Reveal Now 按钮的顶部对齐视口顶部（减去 header 高度）
        // 动态获取 header 实际高度（可能包含未登录横幅 45px）
        const headerContainer = document.querySelector('[class*="headerContainer"]') as HTMLElement;
        const headerHeight = headerContainer?.offsetHeight || 64;
        // 使用 getBoundingClientRect 获取按钮在文档中的绝对位置
        const revealButton = document.querySelector(`.${styles['reveal-button']}`) as HTMLElement;
        const scrollTop = document.scrollingElement?.scrollTop || 0;
        // 按钮顶部在文档中的绝对位置 = 当前滚动位置 + 按钮相对视口的 top
        const buttonAbsoluteTop = revealButton
          ? scrollTop + revealButton.getBoundingClientRect().top
          : dom.offsetTop - 80;
        document.scrollingElement.scrollTo({
          top: buttonAbsoluteTop - headerHeight,
          behavior: 'smooth',
        });

        // 等待滚动完成后再显示内容，避免滚动过程中的 CLS
        let scrollTimer: NodeJS.Timeout;
        const handleScrollEnd = () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            window.removeEventListener('scroll', handleScrollEnd);
            doShowContent();
          }, 100);
        };
        window.addEventListener('scroll', handleScrollEnd);
        handleScrollEnd(); // 立即检查一次，以防不需要滚动

        return;
      }
    } else {
      // pageNo > 1: Load More 逻辑
      if (dataRes?.code === 0) {
        if (isFollowing) {
          setFollowingData({
            items: [...followingData?.items, ...dataRes?.data?.items],
            has_next: dataRes?.data?.has_next,
            code: dataRes?.code,
          });
        } else {
          setFollowerData({
            items: [...followerData?.items, ...dataRes?.data?.items],
            has_next: dataRes?.data?.has_next,
            code: dataRes?.code,
          });
        }
        // Load More 成功后关闭 loading，移除骨架屏
        setIsLoading(false);
        setStage(0);
        return;
      }
    }

    let code = dataRes?.code;

    if (code !== 0) {
      // 错误情况下也需要更新基础状态
      setIsFirstSearch(false);
      setStage(0);

      if (code === 40001) {
        // CLS 优化：未授权时保持骨架屏，显示订阅弹窗（统一体验）
        setIsLoading(false);
        setKeepSkeletonVisible(true);
        setShowSubscriptionLimit(true);
        return;
      } else if (code === 23001) {
        // CLS 优化：需要订阅时保持骨架屏，并直接显示订阅弹窗（统一体验，不区分登录状态）
        setIsLoading(false);
        setKeepSkeletonVisible(true);
        setShowSubscriptionLimit(true);
        return;
      } else if (code === 23002) {
        // 23002: no more updates for now - 关闭 loading，显示弹窗提示
        setIsLoading(false);
        setKeepSkeletonVisible(true); // CLS: 弹窗期间保持骨架屏
        if (data?.is_private == 1) {
          setOpenPrivateAccount({
            title: t('OopsPrivateProfileDetected'),
            desc: (
              <>
                <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                  {t('ThisInstagramAccountIsPrivate_Desc')}
                </div>
                <UserDashboard {...data} />
              </>
            ),
            btnTitle: t('TRYAGAIN'),
            btnDesc: (
              <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
                {t('Note: Only public accounts can be tracked.')}
              </p>
            ),
          });
        } else {
          setOpenPrivateAccount({
            title: t('No Updates Available'),
            desc: t('There are no recent updates for this account. Please check back later.'),
            btnTitle: t('OK'),
          });
        }
        return;
      } else if (code === 23999) {
        setIsLoading(false);
        setKeepSkeletonVisible(true); // CLS: 弹窗期间保持骨架屏
        setOpenRefueingPkg(true);
        return; // CLS: 必须 return，否则后续代码会继续执行导致骨架屏消失
      } else if ([80001, 80002, 80003].includes(code)) {
        setIsLoading(false);
        message.error(t('Verification failed. Please try again.'));
      } else {
        setIsLoading(false);
        setKeepSkeletonVisible(true); // CLS: 弹窗期间保持骨架屏
        setOpenPrivateAccount({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
        });
      }
    } else {
      setHasNoData(false);
    }
  };

  const handleSearch = async ({
    value,
    waitToken = true,
    needTurnstile,
    turnstileToken,
  }: {
    waitToken?: boolean;
    value: string;
    needTurnstile: boolean;
    turnstileToken: string | null;
  }) => {
    if (isLoading) return;
    // CLS 优化：新搜索开始时重置 showContent，确保骨架屏正确显示
    setShowContent(false);
    setFollowerPageNo(1);
    setFollowingPageNo(1);
    setFollowerData(null);
    setFollowingData(null);
    setTurnstileToken('');
    if (!isLogin && waitToken) {
      nextCallRef.current = (needTurnstile: boolean, turnstileToken: string | null) =>
        handleSearch({
          value,
          waitToken: false,
          needTurnstile,
          turnstileToken,
        });
      return;
    }
    setCurrentInsUser(null);
    setHasNoData(false);
    // 移除点击搜索时的滚动，只保留结果返回后的滚动
    if (!turnstileToken && needTurnstile) {
      nextCallRef.current = (needTurnstile: boolean, turnstileToken: string | null) =>
        handleSearch({
          value,
          waitToken: false,
          needTurnstile,
          turnstileToken,
        });
      return message.warning(t('Please verify before continuing.'));
    }
    fetchInsMonitorSearch(value, undefined, { isPremium, bizFuncName: 'recent_follow' })
      .then(async (res) => {
        if (res.code == 0) {
          // CLS 优化：不在这里设置 currentInsUser，而是在 handleReveal 的 doShowContent 中设置
          // 这样所有状态更新在同一帧内完成，避免 CLS
          handleReveal({
            waitToken: false,
            needTurnstile,
            turnstileToken,
            isFollowing: activeTab === 'follows',
            pageNo: 1,
            username: value,
            isBtnClick: false,
            data: res.data[0],
          });
        } else {
          message.error(res.message);
        }
      })
      .catch(() => {});
  };

  const onPayClick = () => {
    if (!isLogin) {
      onOpenLoginModal(true);
      onChangedLoginView(false);
      return;
    }
    setShowSubscriptionLimit(true);
  };

  const onNext = () => {
    if (stage !== 0) {
      return;
    }
    // if (currentList?.has_next) {
    if (activeTab === 'follows') {
      if (followingData?.items?.length > 0) {
        handleReveal({
          waitToken: false,
          needTurnstile,
          turnstileToken,
          isFollowing: true,
          pageNo: followingPageNo + 1,
          username,
          isBtnClick: true,
        });
        setFollowingPageNo(followingPageNo + 1);
      } else {
        handleReveal({
          waitToken: false,
          needTurnstile,
          turnstileToken,
          isFollowing: true,
          pageNo: 1,
          username,
          isBtnClick: true,
        });
        setFollowingPageNo(1);
      }
    } else {
      if (followerData?.items?.length > 0) {
        handleReveal({
          waitToken: false,
          needTurnstile,
          turnstileToken,
          isFollowing: false,
          pageNo: followerPageNo + 1,
          username,
          isBtnClick: true,
        });
        setFollowerPageNo(followerPageNo + 1);
      } else {
        handleReveal({
          waitToken: false,
          needTurnstile,
          turnstileToken,
          isFollowing: false,
          pageNo: 1,
          username,
          isBtnClick: true,
        });
        setFollowerPageNo(1);
      }
    }
    // } else {
    //   if (currentList?.code === 23001) {
    //     onPayClick()
    //   }
    // }
  };

  function onTurnstileSuccess(token: string) {
    setTurnstileToken(token);
    nextCallRef.current?.(needTurnstile, token);
    nextCallRef.current = null;
  }

  function tabChange() {
    setTurnstileToken('');
    turnstileRef.current?.reset();
  }

  const getSocialImages = useMemo(() => {
    return (
      <div className={styles.socialProof}>
        <div className={styles.socialProofContent}>
          <div className={styles.avatars}>
            {getRandomImages().map((item, i) => (
              <div key={item + i} className={styles.avatarWrapper}>
                <img src={imgUrl(item)} alt="" className={styles.avatarIcon} suppressHydrationWarning />
              </div>
            ))}
          </div>
          <p className={styles.text}>18,600+ {t('users trust DolphinRadar')}</p>
        </div>
        <div className={styles.thumbsUp}>
          {[...Array(3)].map((_, i) => (
            <span key={i} role="img" aria-label="thumbs up">
              👍
            </span>
          ))}
        </div>
      </div>
    );
  }, [getRandomImages]);
  return (
    <div className={styles.recentlyFollowedTool} style={{ minHeight: '100vh' }}>
      <h1 style={{ display: 'none', width: 0, height: 0 }}>{t(`See Anyone's IG Recently Followed Recently`)}</h1>

      <main>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.title}>
              {t("See Anyone's IG Recently Followed")}
              <div className={`${styles.subtitleContainer} ${isLangLanguage && styles.langLanguage}`}>
                <span className={styles['gradient-text']}>{t('Recently')}</span>
                <div className={styles['word-cycle-warp']}>
                  <div className={styles['word-cycle']}>
                    {words.map((word, index) => (
                      <div key={index} className={styles['word-text']} style={{ color: word.color }}>
                        {word.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.features}>
            <div className={styles.feature}>
              <Sparkles className={styles.icon} />
              <p>{t('Absolutely Free')}</p>
            </div>
            <div className={styles.feature}>
              <Eye className={styles.icon} />
              <p>{t('100% Anonymous')}</p>
            </div>
            <div className={styles.feature}>
              <Users className={styles.icon} />
              <p>{t('Instant Results')}</p>
            </div>
            <div className={styles.feature}>
              <Clock className={styles.icon} />
              <p>{t('Latest Data')}</p>
            </div>
          </div>

          <div className={styles['input-section']} ref={inputSectionRef}>
            <div className={styles['input-wrapper']}>
              <input
                type="text"
                placeholder={t('Instagram Username (@name)')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!username.trim()) {
                      return;
                    }
                    setIsLoading(true);
                    const value = (e.target as HTMLInputElement).value;
                    handleSearch({ value, waitToken: !isFirstSearch, needTurnstile, turnstileToken });
                  }
                }}
                style={{ fontSize: '16px', background: '#FFF', color: '#000' }}
              />
            </div>

            <button
              className={styles['reveal-button']}
              onClick={() => {
                if (!username.trim()) {
                  return;
                }
                setIsLoading(true);
                handleSearch({ value: username, waitToken: !isFirstSearch, needTurnstile, turnstileToken });
              }}
              disabled={isLoading || stage !== 0}
            >
              {isLoading
                ? t('Processing...')
                : stage === 0
                  ? t('Reveal Now')
                  : stage === 1
                    ? t('Capturing')
                    : stage === 2
                      ? t('Analyzing')
                      : t('Starting Run')}
            </button>

            <div className={`${styles.info}`} id="search-result-user-info">
              <Gift className={styles.icon} />
              <span>
                <span className={styles.highlight}>{t('Absolutely FREE!')}</span>{' '}
                <span className={styles.bold}>{t('Even No Login Required — Account Must Be Public')}</span>
              </span>
            </div>

            {getSocialImages}
          </div>

          {/* CLS 优化：整个搜索结果区域（Turnstile + 骨架屏/结果）统一管理 */}
          <div
            className={cn(styles['results-section'], 'w-[33.875rem] mx-auto max-md:w-full')}
            style={{
              display:
                isLoading || keepSkeletonVisible || (showContent && currentList?.items?.length > 0) || hasNoData
                  ? 'block'
                  : 'none',
            }}
          >
            {/* CLS 优化：Turnstile 在骨架屏上方，验证成功后随骨架屏一起消失 */}
            {needTurnstile &&
              (isLoading || keepSkeletonVisible) &&
              !showContent &&
              (activeTab === 'follows' ? followingPageNo === 1 : followerPageNo === 1) && (
                <div className="flex-center mt-4 min-h-[65px] max-md:mt-3">
                  <Turnstile
                    ref={turnstileRef}
                    key={activeTab}
                    as="aside"
                    siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                    options={{ theme: 'light', language: router.locale }}
                    scriptOptions={{ appendTo: 'body' }}
                    onSuccess={onTurnstileSuccess}
                    onError={() => setTurnstileToken('')}
                    onExpire={() => setTurnstileToken('')}
                  />
                </div>
              )}
            {/* CLS 优化：首次搜索使用完整骨架屏（包含用户信息 + 结果列表）
                关键条件：!showContent 确保只有在 doShowContent 执行后才切换到真实内容
                这样骨架屏消失和内容出现在同一帧内完成，避免 CLS */}
            {(isLoading || keepSkeletonVisible) &&
            !showContent &&
            (activeTab === 'follows' ? followingPageNo === 1 : followerPageNo === 1) ? (
              <ResultsSkeleton activeTab={activeTab} isPremium={isPremium} showUserInfoSkeleton={true} />
            ) : (
              <>
                {/* 当前被搜索的 Instagram 用户的基础信息 */}
                {showContent && currentInsUser && currentInsUser?.is_private == 0 && (
                  <InstagramUserInfo currentInsUser={currentInsUser} className="mt-8 max-md:mt-4" />
                )}
                {/* 已有数据后不再显示 Turnstile */}
                {showContent && (
                  <div className={cn(styles.results, 'mt-8 max-md:mt-4')}>
                    {/* 选项卡 */}
                    <div className={styles.tabs}>
                      <div
                        className={classNames([styles.tab, activeTab === 'follows' && styles.active])}
                        onClick={() => {
                          setActiveTab('follows'); // INP: 立即切换 tab 状态
                          if (!(followingData?.items?.length > 0)) {
                            // CLS: 切换 tab 时如果没有数据，重置 showContent 以显示骨架屏
                            setShowContent(false);
                            // INP: 使用 rAF 确保 tab 状态先渲染，再发起请求
                            requestAnimationFrame(() => {
                              setFollowingPageNo(1);
                              setTurnstileToken('');
                              handleReveal({
                                waitToken: true,
                                needTurnstile,
                                turnstileToken,
                                isFollowing: true,
                                pageNo: 1,
                                username,
                              });
                            });
                          }
                        }}
                      >
                        {t('Recent Follows')}
                      </div>
                      <div
                        className={classNames([styles.tab, activeTab === 'followers' && styles.active])}
                        onClick={() => {
                          setActiveTab('followers'); // INP: 立即切换 tab 状态
                          if (!(followerData?.items?.length > 0)) {
                            // CLS: 切换 tab 时如果没有数据，重置 showContent 以显示骨架屏
                            setShowContent(false);
                            // INP: 使用 rAF 确保 tab 状态先渲染，再发起请求
                            requestAnimationFrame(() => {
                              setFollowerPageNo(1);
                              setTurnstileToken('');
                              handleReveal({
                                waitToken: true,
                                needTurnstile,
                                turnstileToken,
                                isFollowing: false,
                                pageNo: 1,
                                username,
                              });
                            });
                          }
                        }}
                      >
                        {t('Recent Followers')}
                      </div>
                    </div>
                    {/* 搜索结果 */}
                    <div className={cn(styles.content, 'p-8 max-md:p-4')}>
                      <div className="flex items-center gap-4 mb-4">
                        <h2 style={{ marginBottom: 0 }}>
                          @{showUserName} {t('recently')} {activeTab === 'follows' ? 'followed' : 'followed by'}...
                        </h2>
                        <Tooltip
                          title={t('You can click on any user to explore further on Instagram.')}
                          placement="bottomLeft"
                        >
                          <QuestionCircleFilled className="text-[#8E8E8E] cursor-pointer" />
                        </Tooltip>
                      </div>
                      {hasNoData && (
                        <div className="text-center text-black text-[18px] h-[120px] flex items-center justify-center">
                          {activeTab == 'follows'
                            ? t('This account hasn’t followed anyone recently.')
                            : t('This account hasn’t gained any new followers recently.')}
                        </div>
                      )}

                      <div className={styles['user-list']} style={{ marginBottom: '20px' }}>
                        {currentList?.items?.map((user, index) => (
                          <div
                            key={activeTab + index}
                            className={styles.userItem}
                            onClick={() => {
                              window.open(`https://www.instagram.com/${user?.media_name}`, '_blank', 'noreferrer');
                            }}
                          >
                            <span className={classNames([styles.rank, index < 3 && styles['top-3'], 'flex-shrink-0'])}>
                              {index + 1}
                            </span>
                            <div className={cn(styles.avatar, 'flex-shrink-0')}>
                              <LocalImage src={user.media_avatar} alt={user.media_name} width={40} height={40} />
                            </div>
                            <div className={styles['user-info']}>
                              <div className="flex items-center gap-1.5">
                                <p className={styles.username}>@{user.media_name}</p>
                                {user.is_verified && (
                                  <Image
                                    src={'/images/follow/blue-mark.webp'}
                                    alt={'blue-mark'}
                                    width={16}
                                    height={16}
                                  />
                                )}
                                {user.is_private && (
                                  <div className="text-sm leading-[1] px-2 py-1 ml-3 rounded-md text-[#F04444] border border-[#FFD1D1] bg-[#FFE9E9]">
                                    Private
                                  </div>
                                )}
                              </div>
                              <p className={styles['display-name']}>{user.full_name}</p>
                              <p className={styles.stats}></p>
                            </div>
                            <ArrowRight className={styles.arrow} />
                          </div>
                        ))}
                      </div>
                      {/* CLS 优化：分页加载使用骨架屏，keepSkeletonVisible 确保弹窗期间不消失 */}
                      {(isLoading || keepSkeletonVisible) &&
                        (activeTab === 'follows' ? followingPageNo > 1 : followerPageNo > 1) &&
                        currentList?.items?.length > 0 && <LoadMoreSkeleton startIndex={currentList.items.length} />}
                      {currentList?.gender_info && (
                        <div
                          className="mt-6 mb-8 flex justify-center items-center space-x-2 cursor-pointer max-md:mt-[1.25rem] max-md:mb-7"
                          onClick={onNext}
                        >
                          <span className="w-2 h-2 bg-[#C7C7C7] rounded-full"></span>
                          <span className="w-2 h-2 bg-[#C7C7C7] rounded-full"></span>
                          <span className="w-2 h-2 bg-[#C7C7C7] rounded-full"></span>
                        </div>
                      )}
                      {/* 性别分布 */}
                      {currentList?.gender_info && (
                        <GenderDistribution
                          male={currentList?.gender_info?.boys}
                          female={currentList?.gender_info?.girls}
                          other={currentList?.gender_info?.others}
                          onClick={onNext}
                        />
                      )}
                      <div className={styles['load-more']}>
                        <p className="mt-6 text-[1rem] leading-5 max-md:mt-[1.125rem] max-md:text-[calc(0.875rem-1px)] max-md:leading-4">
                          {t('Get complete insights with DolphinRadar Analytics')}
                        </p>
                        <Button
                          className="w-[19.5rem] h-[48px] mt-8 mb-4 rounded-md max-md:w-full max-md:mt-4 max-md:mb-2"
                          loading={isLoading}
                          onClick={onNext}
                        >
                          {currentList?.has_next ? t('Load More Truth') : t('Unlock Full Analytics')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <UserUpdates />
        <div className={styles.container}>
          <FAQSection />
          <HowItWorksSection />
        </div>
        <ContrastCheck />
        <RealStore />
        <ProductionFunction />
        <div className={styles.container}>
          <UserReviewsSection />
          <ExploreMoreToolsSection />
        </div>
      </main>
      {/* CLS: 弹窗关闭时重置 keepSkeletonVisible 和 isLoading */}
      <UpgradeModal
        open={showSubscriptionLimit}
        closeAction={() => {
          setShowSubscriptionLimit(false);
          setKeepSkeletonVisible(false);
          setIsLoading(false);
        }}
      />
      <CommonSimpleModal
        open={openPrivateAccount !== null}
        title={openPrivateAccount?.title}
        desc={openPrivateAccount?.desc}
        btnTitle={openPrivateAccount?.btnTitle}
        closeBtnTitle={openPrivateAccount?.closeBtnTitle}
        btnDesc={openPrivateAccount?.btnDesc}
        closeAction={(...arg) => {
          const res = openPrivateAccount?.action?.(...arg);
          if (res instanceof Promise) {
            res.then(() => {
              setOpenPrivateAccount(null);
              setKeepSkeletonVisible(false);
              setIsLoading(false);
            });
          } else {
            setOpenPrivateAccount(null);
            setKeepSkeletonVisible(false);
            setIsLoading(false);
          }
        }}
      />
      {/* 加油包 - CLS: 弹窗关闭时重置 keepSkeletonVisible 和 isLoading */}
      <RefueingPkg
        open={openRefueingPkg}
        onClose={() => {
          setOpenRefueingPkg(false);
          setKeepSkeletonVisible(false);
          setIsLoading(false);
        }}
      />
    </div>
  );
}
